import { c as _c } from "react/compiler-runtime";
// Third Party
import * as React from 'react';
import Image from 'next/image';
import { useWindowSize } from '@hooks';

// Utils
import { imagekitLoader } from '@utils/imagekit-loader';
import { GRAY_PIXEL_DATA_URL, enumToStringPropertyTypesAbr } from '@utils/constants';
import { concatenateAndReplace } from '@utils/test-helpers';
import { GalleryAsset, Listing, PriceDisplay, PropertyType } from '@gql';
import { formatDateMMMD } from '@utils/date-helpers';
import { abbreviateState } from '@utils/abbreviate-state';

// UI
import { Link } from '@components/link';
import { Icon, IconNames } from '@components/icon';
import { FavoriteButton } from '@components/favorite-button';
import { Carousel } from '@components/carousel';
import { ListingPriceDisplay } from './listing-price-display';
export type ListingPartial = Pick<Listing, 'lat' | 'lng' | 'propertyType' | 'city' | 'stateOrProvinceLongName' | 'slug' | 'name' | 'guestCapacity' | 'numberOfRooms' | 'numberOfBathrooms' | 'minPrice' | 'maxPrice' | 'id' | 'isFavorited' | 'listingLevel' | 'reviewsAverageRating' | 'reviewsCount'> & {
  account: {
    companyName?: string;
  };
  galleryAssets: Array<Pick<GalleryAsset, 'id' | 'url' | 'description'>>;
  priceDisplay?: Pick<PriceDisplay, 'baseAmountCents' | 'checkInDate' | 'checkOutDate' | 'nights' | 'subtotalWithoutTaxesCents'>;
};
type SliderImage = {
  url: string;
};
interface Props {
  listing: ListingPartial;
  isFavorited: boolean;
  closeMapCard: VoidFunction;
  listingRoute: string;
  onFavoritedSuccess?: (isFavorited: boolean) => void;
  displayStay?: boolean;
}
const smallScreenSize = 640;
export const ListingMapCard: React.FC<Props> = t0 => {
  const $ = _c(79);
  const {
    listing,
    isFavorited,
    closeMapCard,
    onFavoritedSuccess,
    listingRoute,
    displayStay: t1
  } = t0;
  const displayStay = t1 === undefined ? false : t1;
  const {
    width: screenWidth
  } = useWindowSize();
  const [isHydrated, setIsHydrated] = React.useState(false);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsHydrated(true);
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  React.useEffect(t2, t3);
  const linkTarget = isHydrated && screenWidth > smallScreenSize ? "_blank" : undefined;
  const name = listing.name || "";
  const href = listingRoute;
  const shouldShowAllImages = listing.galleryAssets.length > 1;
  const shouldShowGuestCapacity = (!displayStay || !listing.priceDisplay) && (listing.numberOfRooms || listing.guestCapacity) && listing.propertyType !== PropertyType.BoutiqueHotel && listing.propertyType !== PropertyType.BedAndBreakfast;
  let result;
  if ($[2] !== listing.galleryAssets) {
    result = [];
    if (Array.isArray(listing.galleryAssets) && listing.galleryAssets.length > 0) {
      listing.galleryAssets.forEach(galleryAsset => {
        if (galleryAsset.url) {
          result.push({
            url: galleryAsset.url
          });
        }
      });
    }
    $[2] = listing.galleryAssets;
    $[3] = result;
  } else {
    result = $[3];
  }
  const images = result;
  const t4 = listing.city;
  let t5;
  if ($[4] !== listing.stateOrProvinceLongName) {
    t5 = abbreviateState(listing.stateOrProvinceLongName);
    $[4] = listing.stateOrProvinceLongName;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const location = `${t4}, ${t5}`;
  let t6;
  if ($[6] !== listing.propertyType) {
    t6 = listing.propertyType ? enumToStringPropertyTypesAbr(listing.propertyType) : null;
    $[6] = listing.propertyType;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const propertyType = t6;
  const t7 = shouldShowAllImages && isHydrated && screenWidth > smallScreenSize;
  let t8;
  if ($[8] !== href || $[9] !== images || $[10] !== linkTarget || $[11] !== listing.name) {
    t8 = images.map((image, index) => <Link to={href || ""} key={index} target={linkTarget} className="w-full h-48 cursor-pointer"><Image height={192} width={336} src={image.url} alt={`${listing.name} - Image ${index + 1}`} placeholder="blur" className="w-auto h-full lg:w-full lg:h-48 object-center object-cover" loader={imagekitLoader} blurDataURL={GRAY_PIXEL_DATA_URL} sizes="400px" data-testid={concatenateAndReplace(`${listing.name}-image-${index}`)} /></Link>);
    $[8] = href;
    $[9] = images;
    $[10] = linkTarget;
    $[11] = listing.name;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== shouldShowAllImages || $[14] !== t7 || $[15] !== t8) {
    t9 = <Carousel showDots={shouldShowAllImages} showArrows={t7} className="w-full h-48 cursor-pointer">{t8}</Carousel>;
    $[13] = shouldShowAllImages;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="flex items-center justify-center w-3 h-3 lg:hidden"><Icon name={IconNames.X} size="xs" solid={true} /></div>;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="hidden lg:flex items-center justify-center w-4 h-4"><Icon name={IconNames.X} solid={true} /></div>;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== closeMapCard) {
    t12 = <div className="absolute top-0 left-0 pl-2 pt-2"><div><button type="button" aria-label="Close listing card" className="inline-flex justify-center text-white opacity-100 hover:opacity-70 focus-visible:ring-2 focus-visible:ring-white font-semibold bg-black/50 p-1.5 rounded-full" onClick={closeMapCard}>{t10}{t11}</button></div></div>;
    $[19] = closeMapCard;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12 || $[22] !== t9) {
    t13 = <div className="relative block h-full w-1/3 lg:w-full lg:aspect-[5/3]">{t9}{t12}</div>;
    $[21] = t12;
    $[22] = t9;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = href || "";
  let t15;
  if ($[24] !== name) {
    t15 = <div className="flex-1 text-primary-main font-semibold text-xs line-clamp-1 min-w-0">{name}</div>;
    $[24] = name;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== listing.reviewsAverageRating || $[27] !== listing.reviewsCount) {
    t16 = listing.reviewsAverageRating ? <div className="flex-shrink-0 text-xs font-medium whitespace-nowrap text-primary-main"><div className="flex flex-row items-center space-x-0.5"><img alt="" aria-hidden="true" src="/assets/svg/ratings/star-yellow.svg" className="w-3 h-3 pr-[1px]" /><div>{Number.isInteger(listing.reviewsAverageRating) ? listing.reviewsAverageRating?.toFixed(1) : listing.reviewsAverageRating?.toFixed(2)}</div>{listing.reviewsCount && <div className="pl-0.5">{"( " + `${listing.reviewsCount}` + " )"}</div>}</div></div> : null;
    $[26] = listing.reviewsAverageRating;
    $[27] = listing.reviewsCount;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t15 || $[30] !== t16) {
    t17 = <div className="flex flex-row w-full justify-between space-x-2 max-w-[calc(100%-10px)] items-center pb-0.5 ">{t15}{t16}</div>;
    $[29] = t15;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const t18 = propertyType ? `${propertyType} in ${location}` : location;
  let t19;
  if ($[32] !== t18) {
    t19 = <div className="flex flex-row w-full justify-between space-x-1 max-w-sm"><div className="flex-shrink text-gray-main text-xs font-light line-clamp-1">{t18}</div></div>;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== listing.guestCapacity || $[35] !== listing.numberOfRooms || $[36] !== shouldShowGuestCapacity) {
    t20 = shouldShowGuestCapacity ? <div className="flex-shrink text-gray-main text-xs font-light line-clamp-1"><div className="flex space-x-1.5">{listing.numberOfRooms ? <div className="flex items-center">{listing.numberOfRooms}{" "}{listing.numberOfRooms > 1 ? " bedrooms" : " bedroom"}</div> : null}{listing.numberOfRooms && listing.guestCapacity ? <div className="flex items-center">•</div> : null}{listing.guestCapacity ? <><div className="flex items-center">{listing.guestCapacity}{" "}{listing.guestCapacity > 1 ? " guests" : " guest"}</div></> : null}</div></div> : null;
    $[34] = listing.guestCapacity;
    $[35] = listing.numberOfRooms;
    $[36] = shouldShowGuestCapacity;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== listing.account.companyName) {
    t21 = listing.account.companyName ? <div className="flex flex-row w-full justify-between space-x-1 max-w-sm"><div className="flex-shrink text-gray-main text-xs font-light line-clamp-1">Hosted by {listing.account.companyName}</div>{" "}</div> : null;
    $[38] = listing.account.companyName;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== displayStay || $[41] !== listing.priceDisplay) {
    t22 = displayStay && listing.priceDisplay ? <div className="flex-shrink line-clamp-1 text-gray-main text-xs font-light text-ellipsis overflow-hidden">{formatDateMMMD(listing.priceDisplay.checkInDate)}{" \u2013 "}{formatDateMMMD(listing.priceDisplay.checkOutDate)}</div> : null;
    $[40] = displayStay;
    $[41] = listing.priceDisplay;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== listing.maxPrice || $[44] !== listing.minPrice || $[45] !== listing.priceDisplay) {
    t23 = <div className="hidden lg:flex text-gray-dark text-xs font-semibold my-0.5"><ListingPriceDisplay priceDisplay={listing.priceDisplay} minPrice={listing.minPrice} maxPrice={listing.maxPrice} /></div>;
    $[43] = listing.maxPrice;
    $[44] = listing.minPrice;
    $[45] = listing.priceDisplay;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t14 || $[48] !== t17 || $[49] !== t19 || $[50] !== t20 || $[51] !== t21 || $[52] !== t22 || $[53] !== t23) {
    t24 = <a href={t14} className="w-full text-gray-dark text-xs font-medium space-y-0.5 items-center justify-center">{t17}{t19}{t20}{t21}{t22}{t23}</a>;
    $[47] = t14;
    $[48] = t17;
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== isFavorited || $[56] !== listing.id || $[57] !== onFavoritedSuccess) {
    t25 = <div className="w-16 -mt-4 -mr-0.5 lg:hidden"><FavoriteButton listingID={listing.id} listingIsFavorited={isFavorited} onFavoritedSuccess={onFavoritedSuccess} /></div>;
    $[55] = isFavorited;
    $[56] = listing.id;
    $[57] = onFavoritedSuccess;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== t24 || $[60] !== t25) {
    t26 = <div className="flex justify-between pt-2.5 w-full">{t24}{t25}</div>;
    $[59] = t24;
    $[60] = t25;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== listing.maxPrice || $[63] !== listing.minPrice || $[64] !== listing.priceDisplay) {
    t27 = <div className="flex pb-2 justify-between pr-3 lg:pb-3"><div className="lg:hidden"><ListingPriceDisplay priceDisplay={listing.priceDisplay} minPrice={listing.minPrice} maxPrice={listing.maxPrice} /></div></div>;
    $[62] = listing.maxPrice;
    $[63] = listing.minPrice;
    $[64] = listing.priceDisplay;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== t26 || $[67] !== t27) {
    t28 = <div className="flex flex-col h-full justify-between">{t26}{t27}</div>;
    $[66] = t26;
    $[67] = t27;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== isFavorited || $[70] !== listing.id || $[71] !== onFavoritedSuccess) {
    t29 = <div className="absolute top-0 right-0 hidden lg:block"><FavoriteButton listingID={listing.id} listingIsFavorited={isFavorited} onFavoritedSuccess={onFavoritedSuccess} /></div>;
    $[69] = isFavorited;
    $[70] = listing.id;
    $[71] = onFavoritedSuccess;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== t28 || $[74] !== t29) {
    t30 = <div className="h-full w-2/3 pl-3 lg:w-full lg:h-full">{t28}{t29}</div>;
    $[73] = t28;
    $[74] = t29;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] !== t13 || $[77] !== t30) {
    t31 = <div className="h-full w-full rounded-2xl overflow-hidden bg-white" role="presentation" onClick={_temp}><div className="flex flex-row h-full lg:flex-col bg-white">{t13}{t30}</div></div>;
    $[76] = t13;
    $[77] = t30;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  return t31;
};
function _temp(e) {
  e.stopPropagation();
}